/**
 * Hinterland: the peaks list in the search panel (snippets/search-modal.liquid) and on the search
 * page ([data-hl-search-page], sections/hl-collection-products.liquid).
 * The peak rows (sections/hl-search-peaks.liquid) are fetched once, when the visitor shows intent
 * (pointer over or focus on a search button), when the panel opens, or on the search page, then
 * filtered here as they type: every word must match the name, range or an alias ("Mt Elbert" finds
 * Mount Elbert), and generic words like "range" are ignored, as on the peaks map. The first 7
 * matches are shown ("Peaks / 7 shown"). Panel goods come from Horizon's predictive search
 * (assets/predictive-search.js); search page goods update through assets/collection-products.js,
 * and the search page adds up to 3 pages from predictive search.
 * The mobile menu's "Search" link ([data-hl-open-search]) closes the menu and opens the panel.
 */

const LIMIT = 7;
const GENERIC_WORDS = new Set(['range', 'ranges', 'mountains', 'mountain', 'mtns', 'mtn', 'the', 'of', 'peak', 'peaks']);
const PAGES_DELAY = 300;

/** @type {HTMLElement[] | null} */
let peaks = null;
/** @type {Promise<void> | null} */
let loading = null;

const searchPage = document.querySelector('[data-hl-search-page]');

function load() {
  loading ??= (async () => {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}?section_id=hl-search-peaks`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      peaks = [...html.querySelectorAll('[data-hl-search-peaks] > li')].map((item) => /** @type {HTMLElement} */ (item));
      render();
      renderPage();
    } catch {
      // Search still finds goods; try the peaks again next time.
      loading = null;
    }
  })();
  return loading;
}

/** The search panel's peaks. */
function render() {
  const panel = document.getElementById('search-modal');
  if (panel) renderPeaks(panel, document.getElementById('cmdk-input'), true);
}

/**
 * Filters the peaks for a field's query into a container's list (first 7 matches).
 * @param {ParentNode} root - Holds [data-hl-search-peaks-group], -list and -heading.
 * @param {HTMLElement | null} input - The search field.
 * @param {boolean} [keyboard] - Add the rows to Horizon's arrow-key navigation (search panel).
 */
function renderPeaks(root, input, keyboard = false) {
  const group = root.querySelector('[data-hl-search-peaks-group]');
  const list = root.querySelector('[data-hl-search-peaks-list]');
  const heading = root.querySelector('[data-hl-search-peaks-heading]');
  if (!peaks || !(group instanceof HTMLElement) || !list || !(heading instanceof HTMLElement)) return;

  const words = (input instanceof HTMLInputElement ? input.value : '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  // The last word may be half-typed ("elk ran"): treat the start of a generic word as generic too.
  const isGeneric = (/** @type {string} */ word, /** @type {number} */ index) =>
    GENERIC_WORDS.has(word) ||
    (index === words.length - 1 && [...GENERIC_WORDS].some((generic) => generic.length > 3 && generic.startsWith(word)));
  const specific = words.filter((word, index) => !isGeneric(word, index));
  const terms = specific.length ? specific : words;

  const matches = peaks.filter((peak) => terms.every((term) => (peak.dataset.search ?? '').includes(term))).slice(0, LIMIT);

  list.replaceChildren(
    ...matches.map((peak) => {
      const item = /** @type {HTMLElement} */ (peak.cloneNode(true));
      // Horizon's arrow-key navigation picks up these rows before the goods.
      if (keyboard) item.setAttribute('ref', 'resultsItems[]');
      return item;
    })
  );
  heading.textContent = (heading.dataset.template ?? '').replace('[count]', String(matches.length));
  group.dataset.count = String(matches.length);
  group.hidden = matches.length === 0;
}

/* ---- Search page ---- */

/** @type {number | null} */
let pagesTimer = null;
/** @type {AbortController | null} */
let pagesRequest = null;

/** The search page's field (the goods search form in the same listing). */
function pageInput() {
  const input = searchPage?.closest('hl-collection-products')?.querySelector('.hl-collection-search input[name="q"]');
  return input instanceof HTMLInputElement ? input : null;
}

/**
 * Updates the search page's peaks now and, when asked, its pages after a short pause.
 * @param {{ pages?: boolean }} [options]
 */
function renderPage({ pages = false } = {}) {
  if (!searchPage) return;
  renderPeaks(searchPage, pageInput());
  if (!pages) return;
  if (pagesTimer !== null) clearTimeout(pagesTimer);
  pagesTimer = window.setTimeout(loadPages, PAGES_DELAY);
}

/** Up to 3 pages for the query (FAQ, About, Shipping…), from predictive search. */
async function loadPages() {
  const container = searchPage?.querySelector('[data-hl-search-pages]');
  if (!(container instanceof HTMLElement)) return;
  const query = pageInput()?.value.trim() ?? '';
  pagesRequest?.abort();
  if (query.length < 2) {
    container.replaceChildren();
    container.hidden = true;
    return;
  }

  const request = new AbortController();
  pagesRequest = request;
  try {
    const url = new URL(window.Theme?.routes?.predictive_search_url ?? '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'page');
    url.searchParams.set('section_id', 'predictive-search');
    const response = await fetch(url, { signal: request.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const group = html.querySelector('[data-search-results] .hl-search__group');
    container.replaceChildren(...(group ? [group] : []));
    container.hidden = !group;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;
    container.hidden = true;
  }
}

/* ---- Events ---- */

// Prefetch on intent, so the list is usually ready when the panel opens.
for (const type of ['pointerover', 'focusin']) {
  document.addEventListener(type, (event) => {
    if (event.target instanceof Element && event.target.closest('search-button, [on\\:click*="search-modal"], [data-hl-open-search]')) load();
  });
}

document.addEventListener('input', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  if (event.target.id === 'cmdk-input') render();
  if (searchPage && event.target === pageInput()) renderPage({ pages: true });
});

// Horizon clears the field without an input event (Clear, Escape, closing the panel).
document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('.hl-search__reset')) queueMicrotask(render);
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && event.target instanceof HTMLElement && event.target.id === 'cmdk-input') {
    setTimeout(render);
  }
});

/** Set while the panel was opened from the mobile menu, so focus goes back to the menu button. */
let openedFromMenu = false;

// The mobile menu's "Search" link: close the menu, then open the panel.
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-hl-open-search]') : null;
  const searchModal = document.getElementById('search-modal');
  if (!link || !searchModal || !('showDialog' in searchModal)) return;
  event.preventDefault();

  const drawer = link.closest('header-drawer');
  if (drawer && 'close' in drawer && typeof drawer.close === 'function') drawer.close();
  openedFromMenu = true;
  load();
  requestAnimationFrame(() => /** @type {any} */ (searchModal).showDialog());
});

const dialog = document.querySelector('#search-modal dialog');
if (dialog) {
  new MutationObserver(() => {
    if (dialog.hasAttribute('open')) {
      load();
      render();
      // Start in the search field (the close button comes first in the panel, so the dialog
      // would otherwise focus it), as in the prototype.
      requestAnimationFrame(() => {
        const input = document.getElementById('cmdk-input');
        if (input instanceof HTMLInputElement && dialog.contains(document.activeElement) && document.activeElement !== input) {
          input.focus();
        }
      });
    } else if (openedFromMenu) {
      openedFromMenu = false;
      const menuButton = document.querySelector('header-drawer summary');
      if (menuButton instanceof HTMLElement) menuButton.focus({ preventScroll: true });
    }
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
}

// The search page shows its peaks (and pages for a shared ?q=) straight away.
if (searchPage) {
  load();
  renderPage({ pages: true });
}
